import { SheetWithFormComponent } from '@/components/ui/SheetWithFormComponent'
import { RestSchemas, useRestClient } from '@/contexts/QueryProvider'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { FormComponentLoading } from '@/catalog-apps/components/component-library/FormComponent'

import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { ObjectListField } from '@/components/form-components/ObjectListField'

type ExistingRegisteredProvider = RestSchemas['Messages.Agent.ExistingRegisteredProvider']
type NewRegisteredProvider = RestSchemas['Messages.Agent.NewRegisteredProvider']
type ProviderModel = RestSchemas['Messages.Agent.ProviderModel']

const optionalNonNegativeNumber = z.preprocess(
  (value) => {
    if (value === '' || value === undefined || value === null) {
      return undefined
    }
    const parsed = Number(value)
    return Number.isNaN(parsed) ? undefined : parsed
  },
  z.number().min(0).optional()
)

const providerModelSchema = z.object({
  model: z.string().min(1, 'Model is required'),
  name: z.string().min(1, 'Model name is required'),
  inputCostPerMillionTokens: optionalNonNegativeNumber,
  outputCostPerMillionTokens: optionalNonNegativeNumber,
  cachedInputCostPerMillionTokens: optionalNonNegativeNumber,
})

const mapFormModelsToApi = (models: z.infer<typeof providerModelSchema>[]): ProviderModel[] =>
  models.map((model) => ({
    model: model.model,
    name: model.name,
    inputCostPerMillionTokens: model.inputCostPerMillionTokens ?? null,
    outputCostPerMillionTokens: model.outputCostPerMillionTokens ?? null,
    cachedInputCostPerMillionTokens: model.cachedInputCostPerMillionTokens ?? null,
  }))

const formSchema = z.object({
  name: z.string().min(1, 'Name is required').max(120, 'Name cannot exceed 120 characters'),
  endpoint: z.string().max(1024, 'Endpoint cannot exceed 1024 characters').optional().default(''),
  description: z.string().max(256, 'Description cannot exceed 256 characters').optional().default(''),
  models: z.array(providerModelSchema).optional().default([]),
})

export type TProviderForm = z.infer<typeof formSchema>

type ProviderFormProps = {
  onSubmit?: () => void
  sheetTrigger?: React.ReactNode
  sheetTriggerTooltip?: string
  sheetTitle?: string
  sheetDescription?: string
  openInSheet?: boolean
  selectedProvider?: ExistingRegisteredProvider
}

export const ProviderForm = (props: ProviderFormProps) => {
  const [sheetOpen, setSheetOpen] = useState(false)
  const edit = !!props.selectedProvider

  const {
    getProviderRegistrationApiListProviders,
    postProviderRegistrationApiUpdateProvider,
    putProviderRegistrationApiAddProvider,
  } = useRestClient()

  const { mutate: updateProvider, isPending: isUpdating } = postProviderRegistrationApiUpdateProvider.useMutation(
    undefined,
    {
      onSuccess: () => {
        getProviderRegistrationApiListProviders.invalidateQueries(undefined)
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Provider successfully updated')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error updating provider')
      },
    }
  )

  const { mutate: addProvider, isPending: isAdding } = putProviderRegistrationApiAddProvider.useMutation(
    undefined,
    {
      onSuccess: () => {
        getProviderRegistrationApiListProviders.invalidateQueries(undefined)
        if (props.sheetTrigger) {
          setSheetOpen(false)
        }
        toast.success('Provider successfully added')
        props.onSubmit?.()
      },
      onError: () => {
        toast.error('Error adding provider')
      },
    }
  )

  const submitHandler = useCallback(
    (data: TProviderForm) => {
      if (edit && props.selectedProvider) {
        const updatedProvider: ExistingRegisteredProvider = {
          id: props.selectedProvider.id,
          name: data.name,
          key: props.selectedProvider.key ?? data.name.toLowerCase().replace(/\s+/g, '-'),
          endpoint: data.endpoint || null,
          description: data.description || null,
          models: mapFormModelsToApi(data.models ?? []),
          createdAt: props.selectedProvider.createdAt,
          updatedAt: props.selectedProvider.updatedAt,
          whodunit: props.selectedProvider.whodunit,
        }

        updateProvider({
          body: updatedProvider,
        })
      } else {
        const newProvider: NewRegisteredProvider = {
          name: data.name,
          key: data.name.toLowerCase().replace(/\s+/g, '-'),
          endpoint: data.endpoint || null,
          description: data.description || null,
          models: mapFormModelsToApi(data.models ?? []),
        }

        addProvider({
          body: newProvider,
        })
      }
    },
    [edit, props.selectedProvider, addProvider, updateProvider]
  )

  const isLoading = isUpdating || isAdding
  if (isLoading) return <FormComponentLoading />

  return props.sheetTrigger || props.openInSheet ? (
    <SheetWithFormComponent
      title={props.sheetTitle}
      description={props.sheetDescription}
      trigger={props.sheetTrigger}
      triggerTooltip={props.sheetTriggerTooltip}
      isOpen={sheetOpen}
      onOpenChange={setSheetOpen}
    >
      <FormComponent {...props} submitHandler={submitHandler} />
    </SheetWithFormComponent>
  ) : (
    <FormComponent {...props} submitHandler={submitHandler} />
  )
}

const FormComponent = (
  props: ProviderFormProps & {
    submitHandler: (data: TProviderForm) => void
  }
) => {
  const selectedProvider: Partial<ExistingRegisteredProvider> = props.selectedProvider ?? {}
  const { name, endpoint, description, models } = selectedProvider
  const edit = !!props.selectedProvider

  const defaultValues = useMemo((): TProviderForm => {
    return {
      name: name ?? '',
      endpoint: endpoint ?? '',
      description: description ?? '',
      models: (models ?? []).map((m) => ({
        model: m.model,
        name: m.name,
        inputCostPerMillionTokens: m.inputCostPerMillionTokens ?? undefined,
        outputCostPerMillionTokens: m.outputCostPerMillionTokens ?? undefined,
        cachedInputCostPerMillionTokens: m.cachedInputCostPerMillionTokens ?? undefined,
      })),
    }
  }, [name, endpoint, description, models])

  const form = useForm<TProviderForm>({
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const handleInvalidSubmit = useCallback(() => {
    toast.error('Please fix validation errors before submitting')
  }, [])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(props.submitHandler, handleInvalidSubmit)}>
        <div className="flex h-full flex-col">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="endpoint"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Endpoint</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <ObjectListField
              control={form.control}
              name="models"
              label="Models"
              properties={[
                {
                  id: 'model',
                  title: 'Model',
                  type: 'string',
                  required: true,
                  placeholder: 'e.g. openai.gpt-4.1',
                },
                {
                  id: 'name',
                  title: 'Display Name',
                  type: 'string',
                  required: true,
                  placeholder: 'e.g. GPT-4.1',
                },
                {
                  id: 'inputCostPerMillionTokens',
                  title: 'In $/1M',
                  type: 'number',
                  placeholder: 'e.g. 2.50',
                },
                {
                  id: 'outputCostPerMillionTokens',
                  title: 'Out $/1M',
                  type: 'number',
                  placeholder: 'e.g. 10.00',
                },
                {
                  id: 'cachedInputCostPerMillionTokens',
                  title: 'Cached in $/1M',
                  type: 'number',
                  placeholder: 'e.g. 1.25',
                },
              ]}
              emptyStateMessage='No models yet. Click "Add row" to create one.'
            />
          </div>
          <div className="form-action-container">
            <Button type="submit">{edit ? 'Update' : 'Create'}</Button>
          </div>
        </div>
      </form>
    </Form>
  )
}
